import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Divider, Stack, TextField } from '@mui/material'
import { useStyles } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { convertExpressionToDisplayExpression } from '../../../util/expression'
import { selectAlertModalData } from '../../../selectors/alerts'
import { getTimeBucketDisplayName } from '../../../util/timeBuckets'
import AlertExpressionDropdown from '../alertExpressionDropdown/alertExpressionDropdown'
import {
    AlertExpression,
    AlertExpression_Type,
    Condition_SecurityAlertCondition,
    Condition_SecurityAlertCondition_Operator,
    UserAlert_Trigger
} from '../../../proto/generated/nexus'
import { setUserAlertCondition, updateModalUserAlert } from '../../../reducer/alerts.reducer'
import AlertOperatorDropdown from '../alertOperatorDropdown/alertOperatorDropdown'
import { getOperatorSource, getSelectedValue } from '../../../util/alerts'
import { selectIndicators } from '../../../selectors/chart'
import AlertTriggerInput from '../alertTriggerInput/alertTriggerInput'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs, { Dayjs } from 'dayjs'
import AlertMessageInput from '../alertMessageInput/alertMessageInput'
import { FixedExpressionOutputTypes } from '../alertExpressionDropdown/alertExpressionDropdown.typings'
import { TimeBucket_TimeSeries } from '../../../proto/generated/common'

export type AlertSettingsTabProps = {
    onValidation: (error: boolean) => void
}

const AlertSettingsTab: React.FC<AlertSettingsTabProps> = ({ onValidation }) => {

    const dispatch = useDispatch()
    const { classes } = useStyles()
    const indicators = useSelector(selectIndicators)
    const { chartSnapshot, alert, edit } = useSelector(selectAlertModalData)
    const { securityList, expression, expressionMode, timeBucket } = chartSnapshot
    const [primarySecurity, setPrimarySecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [expressionSecurities, setExpressionSecurities] = useState<Awaited<ReturnType<typeof getSecurityAsync>>[]>([])
    useEffect(() => {
        if (!securityList.length) {
            setPrimarySecurity(undefined)
            setExpressionSecurities([])
            return
        }
        if (!expressionMode) {
            getSecurityAsync(getSecurityKey(securityList[0].exchange, securityList[0].code)).then(setPrimarySecurity)
            setExpressionSecurities([])
        } else {
            Promise.all(securityList.map((v) => getSecurityAsync(getSecurityKey(v.exchange, v.code)))).then(setExpressionSecurities)
            setPrimarySecurity(undefined)
        }
    }, [securityList, expressionMode])

    const symbolsDisplayValue = useMemo(() => {
        if (!securityList.length) return ''
        let elements = ''
        if (!expressionMode && primarySecurity) {
            elements += primarySecurity.displayName
        } else if (expressionMode && expression.length && expressionSecurities.length) {
            elements += convertExpressionToDisplayExpression(expression, expressionSecurities)
        }
        if (!elements.length) return ''
        elements += `, ${getTimeBucketDisplayName(timeBucket)}`
        return elements
    }, [securityList, expression, expressionMode, timeBucket, primarySecurity, expressionSecurities])

    const lhsExpression = alert?.condition?.securityAlertCondition?.lhsExpression
    const rhsExpression = alert?.condition?.securityAlertCondition?.rhsExpression
    const operator = alert?.condition?.securityAlertCondition?.operator

    const onLhsExpressionChange = useCallback((exp: AlertExpression, change: keyof AlertExpression) => {
        if (!rhsExpression || !operator) return
        const condition: Condition_SecurityAlertCondition = {
            lhsExpression: exp,
            operator,
            rhsExpression
        }
        const lhsValue = getSelectedValue(exp, indicators)


        if (lhsValue[0] === FixedExpressionOutputTypes.PRICE.valueOf()) {
            if (exp.simpleSecurity) {
                exp.simpleSecurity.source = getOperatorSource(operator)
            } else if (exp.spreadSecurity) {
                exp.spreadSecurity.source = getOperatorSource(operator)
            }
            exp.timeBucket = {
                timeSeries: TimeBucket_TimeSeries.SECOND,
                value: 1
            }
        }

        const rhsValue = getSelectedValue(rhsExpression, indicators)

        if (rhsValue[0] === lhsValue[0] && rhsValue[1] === lhsValue[1]) {
            const indicator = chartSnapshot.indicators.find((v => v.code === rhsExpression.indicator?.indicatorCode))
            if (indicator && indicator.outputs.length > 0) {
                // If only 1 output is present, then change rhs to fixed value type
                // Else, set rhs output to other than current one
                const newOutput = indicator.outputs.filter(o => o.title !== rhsExpression.indicator!.output)[0]
                condition.rhsExpression = {
                    type: newOutput ? rhsExpression.type : AlertExpression_Type.FIXED_VALUE,
                    fixedValue: !newOutput ? 0 : undefined,
                    indicator: newOutput ? {
                        ...rhsExpression.indicator!,
                        output: newOutput.title
                    } : undefined,
                    timeBucket: undefined
                }
            }
        }

        if (condition.rhsExpression!.type === AlertExpression_Type.FIXED_VALUE) {
            // change value to the latest one from snapshot
            if (lhsValue[0] === FixedExpressionOutputTypes.PRICE.valueOf()) {
                condition.rhsExpression = {
                    ...condition.rhsExpression!,
                    fixedValue: chartSnapshot.data.close
                }
            } else if (lhsValue[0] === FixedExpressionOutputTypes.VOLUME.valueOf()) {
                condition.rhsExpression = {
                    ...condition.rhsExpression!,
                    fixedValue: chartSnapshot.data.customValues?.volume as number ?? 0
                }
            } else {
                const indicatorTitle = indicators.find(v => v.code === exp.indicator?.indicatorCode)?.outputs?.find(o => o.code === lhsValue[1])?.title
                const indicator = chartSnapshot.indicators.find((v => v.code === exp.indicator?.indicatorCode))
                const fixedValue = indicator!.outputValues[indicatorTitle]?.value ?? 0
                condition.rhsExpression = {
                    ...condition.rhsExpression!,
                    fixedValue: Number(fixedValue.toPrecision(2).replace(/\.0+$/, ''))
                }

            }
        }
        dispatch(setUserAlertCondition(condition))
    }, [rhsExpression, operator, indicators, chartSnapshot])


    const onRhsExpressionChange = useCallback((exp: AlertExpression, change: keyof AlertExpression) => {
        if (!operator || !lhsExpression) return
        const condition: Condition_SecurityAlertCondition = {
            lhsExpression,
            operator,
            rhsExpression: exp
        }

        if (exp.type === AlertExpression_Type.FIXED_VALUE && change !== 'fixedValue') {
            const lhsValue = getSelectedValue(lhsExpression, indicators)
            // change value to the latest one from snapshot
            if (lhsValue[0] === FixedExpressionOutputTypes.PRICE.valueOf()) {
                condition.rhsExpression = {
                    ...exp,
                    fixedValue: chartSnapshot.data.close
                }
            } else if (lhsValue[0] === FixedExpressionOutputTypes.VOLUME.valueOf()) {
                condition.rhsExpression = {
                    ...exp,
                    fixedValue: chartSnapshot.data.customValues?.volume as number ?? 0
                }
            } else {
                const indicatorTitle = indicators.find(v => v.code === lhsExpression.indicator?.indicatorCode)?.outputs?.find(o => o.code === lhsValue[1])?.title
                const indicator = chartSnapshot.indicators.find((v => v.code === lhsExpression.indicator?.indicatorCode))
                const fixedValue = indicator!.outputValues[indicatorTitle]?.value ?? 0
                condition.rhsExpression = {
                    ...exp,
                    fixedValue: Number(fixedValue.toPrecision(2).replace(/\.0+$/, ''))
                }

            }
        }
        dispatch(setUserAlertCondition(condition))
    }, [lhsExpression, operator, indicators, chartSnapshot])

    const onOperatorChange = useCallback((operator: Condition_SecurityAlertCondition_Operator) => {
        if (!operator || !lhsExpression || !rhsExpression) return
        const lhsValue = getSelectedValue(lhsExpression, indicators)
        const newLhsExpression = { ...lhsExpression }
        if (lhsValue[0] === FixedExpressionOutputTypes.PRICE) {
            if (newLhsExpression.type === AlertExpression_Type.SIMPLE_SECURITY) {
                newLhsExpression.simpleSecurity = {
                    ...lhsExpression.simpleSecurity!,
                    source: getOperatorSource(operator)
                }
            } else if (newLhsExpression.type === AlertExpression_Type.SPREAD_SECURITY) {
                newLhsExpression.spreadSecurity = {
                    ...lhsExpression.spreadSecurity!,
                    source: getOperatorSource(operator)
                }
            }
        }
        const condition: Condition_SecurityAlertCondition = {
            lhsExpression: newLhsExpression,
            operator,
            rhsExpression
        }

        dispatch(setUserAlertCondition(condition))
    }, [lhsExpression, rhsExpression])

    const onLhsOutputChange = useCallback((output: string) => {
        if (!lhsExpression || !rhsExpression || !operator) return

        const condition: Condition_SecurityAlertCondition = {
            lhsExpression: {
                ...lhsExpression,
                indicator: {
                    ...lhsExpression.indicator!,
                    output
                }
            },
            operator,
            rhsExpression
        }

        const lhsValue = getSelectedValue(condition.lhsExpression!, indicators)

        if (condition.rhsExpression!.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
            const rhsValue = getSelectedValue(rhsExpression, indicators)


            if (rhsValue[0] === lhsValue[0] && rhsValue[1] === output) {
                const indicator = chartSnapshot.indicators.find((v => v.code === rhsExpression.indicator?.indicatorCode))
                if (indicator) {
                    const newOutput = indicator.outputs.filter(o => o.title !== output)[0]
                    condition.rhsExpression = {
                        ...condition.rhsExpression!,
                        indicator: newOutput ? {
                            ...rhsExpression.indicator!,
                            output: newOutput.title
                        } : undefined,
                        timeBucket: undefined
                    }
                }
            }
        }

        if (condition.rhsExpression!.type === AlertExpression_Type.FIXED_VALUE) {
            if (lhsValue[0] === FixedExpressionOutputTypes.PRICE) {
                condition.rhsExpression = {
                    ...condition.rhsExpression!,
                    fixedValue: chartSnapshot.data.close
                }
            } else {
                const indicatorTitle = indicators.find(v => v.code === condition?.lhsExpression?.indicator?.indicatorCode)?.outputs?.find(o => o.code === lhsValue[1])?.title
                const indicator = chartSnapshot.indicators.find((v => v.code === condition?.lhsExpression?.indicator?.indicatorCode))
                const fixedValue = indicator!.outputValues[indicatorTitle]?.value ?? 0
                condition.rhsExpression = {
                    ...condition.rhsExpression!,
                    fixedValue: Number(fixedValue.toPrecision(2).replace(/\.0+$/, ''))
                }

            }
        }

        dispatch(setUserAlertCondition(condition))

    }, [lhsExpression, rhsExpression, operator, onLhsExpressionChange, indicators, chartSnapshot])

    const onRhsOutputChange = useCallback((output: string) => {
        if (!lhsExpression || !rhsExpression || !operator) return

        const condition: Condition_SecurityAlertCondition = {
            operator,
            lhsExpression,
            rhsExpression: {
                ...rhsExpression,
                indicator: {
                    ...rhsExpression.indicator!,
                    output
                }
            }
        }
        dispatch(setUserAlertCondition(condition))

    }, [lhsExpression, rhsExpression, operator, onRhsExpressionChange])

    const onTriggerChange = useCallback((trigger: UserAlert_Trigger) => {
        dispatch(updateModalUserAlert({
            trigger
        }))
    }, [])

    const onMessageChange = useCallback((message: string) => {
        dispatch(updateModalUserAlert({
            message
        }))
    }, [])

    const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateModalUserAlert({
            name: event.target.value
        }))
    }, [])

    const onDateError = useCallback(() => onValidation(true), [])
    const onDateAccept = useCallback(() => onValidation(false), [])
    const onDateChange = useCallback((date: Dayjs | null) => {
        date && dispatch(updateModalUserAlert({
            expiry: date.valueOf()
        }))
    }, [])

    return <Box>
        <Stack direction="row" className={classes.row}>
            <label className={classes.label}>Symbols</label>
            <TextField value={symbolsDisplayValue} size="small" className={classes.value} disabled />
        </Stack>
        <Stack direction="row">
            <label className={classes.label}>Condition</label>
            {operator && lhsExpression && rhsExpression && (
                <Box className={classes.value}>
                    <AlertExpressionDropdown
                        expression={lhsExpression}
                        chartSnapshot={chartSnapshot}
                        comparedExpression={rhsExpression}
                        mode="lhs"
                        onExpressionChange={onLhsExpressionChange}
                        onOutputChange={onLhsOutputChange}
                        className={classes.row}
                    />
                    <AlertOperatorDropdown onOperatorChange={onOperatorChange} className={classes.row}
                                           value={operator} />
                    <AlertExpressionDropdown
                        expression={rhsExpression}
                        chartSnapshot={chartSnapshot}
                        comparedExpression={lhsExpression}
                        mode="rhs"
                        onExpressionChange={onRhsExpressionChange}
                        onOutputChange={onRhsOutputChange}
                    />
                </Box>)}
        </Stack>

        {alert && alert.condition && <>
            <Divider className={classes.divider} />
            <Stack direction="row">
                <label className={classes.label}>Trigger</label>
                <Box className={classes.value}>
                    <AlertTriggerInput onTriggerChange={onTriggerChange} trigger={alert.trigger}
                                       condition={alert.condition} />
                </Box>
            </Stack>
        </>}

        {alert && alert.expiry && <>
            <Divider className={classes.divider} />
            <Stack direction={'row'}>
                <label className={classes.label}>Expiration</label>
                <DateTimePicker
                    className={classes.value}
                    value={dayjs(alert?.expiry)}
                    ampm
                    format={'MMM DD, YYYY HH:mm A'}
                    disablePast
                    slotProps={{
                        textField: {
                            size: 'small'
                        },
                        field: {
                            readOnly: true
                        }
                    }}
                    onChange={onDateChange}
                    onError={onDateError}
                    onAccept={onDateAccept}
                />
            </Stack>
        </>}
        {alert && alert.condition?.securityAlertCondition && <>
            <Divider className={classes.divider} />

            <Stack direction="row" className={classes.row}>
                <label className={classes.label}>Alert Name</label>
                <TextField value={alert.name} size="small" className={classes.value} onChange={onNameChange} />
            </Stack>
            <Stack direction={'row'}>
                <label className={classes.label}>Message</label>
                <AlertMessageInput
                    condition={alert.condition?.securityAlertCondition}
                    message={alert.message}
                    symbolDisplayName={symbolsDisplayValue}
                    onChange={onMessageChange}
                    className={classes.value}
                    edit={edit}
                />
            </Stack>
        </>}
    </Box>
}

export default memo(AlertSettingsTab)